import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PersistedOrderPayload } from '@onesunday/domain';
import { fetchAnalyticsInsights, fetchEvents, AnalyticsInsightsResponse, PersistedEventLite } from '@onesunday/api-client';

interface InsightsTabProps {
  orders: PersistedOrderPayload[];
  dateFrom: string;
  dateTo: string;
  showTestOrders: boolean;
}

type DrillField = 'item' | 'channel' | 'device' | 'location' | 'city' | 'connection' | 'status' | 'leadDays' | 'day';
interface DrillKey {
  field: DrillField;
  value: string | number;
  label: string;
}

const PALETTE = ['#ea580c', '#dc2626', '#ca8a04', '#0891b2', '#7c3aed', '#16a34a', '#db2777', '#475569'];
const FUNNEL_COLORS = ['#ea580c', '#e85316', '#e0451c', '#d6371d', '#c0392b'];
const PAID_STATUSES = new Set(['Paid', 'Ready', 'Completed']);
const SAST_OFFSET_MS = 2 * 60 * 60 * 1000;

// The reporting calendar is Africa/Johannesburg. The dates sent to the API
// must be UTC instants so Cosmos string comparisons include the complete SAST
// day, rather than starting two hours late.
const sastStartIso = (day: string) => day ? new Date(`${day}T00:00:00+02:00`).toISOString() : undefined;
const sastEndIso = (day: string) => day ? new Date(`${day}T23:59:59.999+02:00`).toISOString() : undefined;

const fmtR = (n: number) => 'R' + Math.round(n).toLocaleString('en-ZA');
const pct = (n: number, d: number) => (d ? ((n / d) * 100).toFixed(1) + '%' : '—');

const isTestOrder = (o: PersistedOrderPayload) =>
  o.yocoTestMode === true || (o.yocoTestMode as unknown) === 'true';

const toSast = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getTime() + SAST_OFFSET_MS);
};
const sastDay = (iso?: string | null) => {
  const d = toSast(iso);
  return d ? d.toISOString().slice(0, 10) : null;
};

// Thin attribution shape so the same helpers work for orders (read from
// metadata.client) and for sessions reconstructed from events.
interface AttributionInput {
  utmSource?: string | null;
  referrer?: string | null;
  userAgent?: string | null;
  timezone?: string | null;
  language?: string | null;
  geoCountry?: string | null;
  geoCity?: string | null;
  connectionEffectiveType?: string | null;
}

const channelOfRaw = ({ utmSource, referrer }: AttributionInput): string => {
  const src = (utmSource || '').toLowerCase();
  if (src) {
    if (src === 'x' || src === 'twitter') return 'X / Twitter';
    return src.charAt(0).toUpperCase() + src.slice(1);
  }
  if (!referrer) return 'Direct';
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    if (host.includes('t.co')) return 'X / Twitter';
    if (host.includes('instagram')) return 'Instagram';
    if (host.includes('facebook')) return 'Facebook';
    if (host.includes('whatsapp') || host.includes('wa.me')) return 'WhatsApp';
    if (host.includes('yoco')) return 'Yoco return';
    return host;
  } catch {
    return 'Unknown';
  }
};

const deviceOfRaw = ({ userAgent }: AttributionInput): string => {
  const u = (userAgent || '').toLowerCase();
  if (u.includes('iphone')) return 'iOS phone';
  if (u.includes('android') && u.includes('mobile')) return 'Android phone';
  if (u.includes('ipad')) return 'iPad';
  if (u.includes('windows')) return 'Windows';
  if (u.includes('mac os')) return 'Mac';
  return 'Other';
};

const channelOf = (o: PersistedOrderPayload): string =>
  channelOfRaw({
    utmSource: o.metadata?.client?.utm?.utm_source,
    referrer: o.metadata?.client?.referrer,
  });

const deviceOf = (o: PersistedOrderPayload): string =>
  deviceOfRaw({ userAgent: o.metadata?.client?.userAgent });

// Country-level location. Prefers geo (IP-based, accurate) over timezone
// (coarse — SA shares one TZ across all cities). Falls back to a timezone
// region grouping when geo is missing (older sessions captured before geo
// was added).
const locationOfRaw = ({ geoCountry, timezone }: AttributionInput): string => {
  if (geoCountry && geoCountry.trim()) return geoCountry.trim();
  const tz = (timezone || '').toLowerCase();
  if (!tz) return 'Unknown';
  if (tz === 'africa/johannesburg' || tz === 'africa/cape_town') return 'South Africa';
  if (tz.startsWith('africa/')) return 'Africa (other)';
  if (tz.startsWith('europe/')) return 'Europe';
  if (tz.startsWith('america/')) return 'Americas';
  if (tz.startsWith('asia/')) return 'Asia';
  if (tz.startsWith('australia/') || tz.startsWith('pacific/')) return 'Oceania';
  return 'Other';
};

// City-level breakdown — only meaningful when geoCity is captured. Returns
// null when missing so we can hide cities for sessions that lack geo data.
const cityOfRaw = ({ geoCity, geoCountry }: AttributionInput): string | null => {
  const city = (geoCity || '').trim();
  if (!city) return null;
  const country = (geoCountry || '').trim();
  return country ? `${city}, ${country}` : city;
};

const locationOf = (o: PersistedOrderPayload): string =>
  locationOfRaw({
    timezone: o.metadata?.client?.timezone,
    geoCountry: o.metadata?.client?.geo?.country,
  });

const cityOf = (o: PersistedOrderPayload): string | null =>
  cityOfRaw({
    geoCity: o.metadata?.client?.geo?.city,
    geoCountry: o.metadata?.client?.geo?.country,
  });

// Connection bucket from the Network Information API. Most browsers report
// "4g" / "3g" / "2g" / "slow-2g" — a few report nothing at all (Safari
// historically). We surface the bucket directly; "Unknown" when missing.
const connectionOfRaw = ({ connectionEffectiveType }: AttributionInput): string => {
  const t = (connectionEffectiveType || '').toLowerCase();
  if (!t) return 'Unknown';
  if (t === '4g') return '4G';
  if (t === '3g') return '3G';
  if (t === '2g') return '2G';
  if (t === 'slow-2g') return 'Slow 2G';
  return t;
};

const connectionOf = (o: PersistedOrderPayload): string =>
  connectionOfRaw({ connectionEffectiveType: o.metadata?.client?.connection?.effectiveType });

interface PieLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  value: number;
  name: string;
}
const renderPieLabel =
  (kind: 'percent' | 'value' | 'both') =>
  ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value }: PieLabelProps) => {
    if (percent < 0.04) return null;
    const RADIAN = Math.PI / 180;
    const r = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    const text =
      kind === 'percent' ? `${(percent * 100).toFixed(0)}%`
      : kind === 'value' ? String(value)
      : `${value} · ${(percent * 100).toFixed(0)}%`;
    return (
      <text
        x={x}
        y={y}
        fill="#ffffff"
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontSize: 12,
          fontWeight: 700,
          paintOrder: 'stroke',
          stroke: 'rgba(28,25,23,0.45)',
          strokeWidth: 3,
          strokeLinejoin: 'round',
        }}
      >
        {text}
      </text>
    );
  };

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`rounded-xl border border-admin-text/12 bg-admin-surface p-4 shadow-sm ${className || ''}`}>
    {children}
  </div>
);

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  height?: number;
}> = ({ title, subtitle, children, footer, height = 280 }) => (
  <Card>
    <div className="text-sm font-semibold text-admin-text">{title}</div>
    {subtitle && <div className="text-xs text-admin-text/60 mb-3">{subtitle}</div>}
    {!subtitle && <div className="mb-3" />}
    <div style={{ width: '100%', height }}>{children}</div>
    {footer && <div className="mt-3 pt-3 border-t border-admin-text/5">{footer}</div>}
  </Card>
);

interface BreakdownRow {
  name: string;
  color: string;
  value: number;
  count?: number;
  total: number;
}
const Breakdown: React.FC<{ rows: BreakdownRow[]; valueFmt: (v: number) => string }> = ({ rows, valueFmt }) => (
  <ul className="space-y-1.5 text-xs">
    {rows.map((r, i) => (
      <li key={i} className="flex items-center gap-2">
        <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: r.color }} />
        <span className="flex-1 text-admin-text truncate">{r.name}</span>
        <span className="tabular-nums font-medium text-admin-text">{valueFmt(r.value)}</span>
        {r.count != null && (
          <span className="tabular-nums text-admin-text/60">
            · {r.count} order{r.count === 1 ? '' : 's'}
          </span>
        )}
        <span className="tabular-nums text-admin-text/50 w-9 text-right">
          {r.total ? Math.round((r.value / r.total) * 100) : 0}%
        </span>
      </li>
    ))}
  </ul>
);

const Kpi: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Card>
    <div className="text-[11px] uppercase tracking-wider font-semibold text-admin-text/60">{label}</div>
    <div className="text-2xl font-bold tracking-tight text-admin-text mt-1">{value}</div>
    {hint && <div className="text-xs text-admin-text/60 mt-0.5">{hint}</div>}
  </Card>
);

export const InsightsTab: React.FC<InsightsTabProps> = ({ orders, dateFrom, dateTo, showTestOrders }) => {
  const [events, setEvents] = useState<PersistedEventLite[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsInsightsResponse | null>(null);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [drill, setDrill] = useState<DrillKey | null>(null);
  // Population mode for the Channel + Device row. "paid" weights by revenue;
  // "orders" counts every paid + unpaid order; "sessions" counts unique
  // sessions reconstructed from events (so visitors who never ordered are
  // included).
  const [populationMode, setPopulationMode] = useState<'paid' | 'orders' | 'sessions'>('paid');

  useEffect(() => {
    let cancelled = false;
    const startISO = sastStartIso(dateFrom);
    const endISO = sastEndIso(dateTo);
    setEventsLoading(true);
    setEventsError(null);
    fetchEvents({ startDate: startISO, endDate: endISO, pageSize: 20000 })
      .then((res) => {
        if (!cancelled) {
          setEvents(res.results || []);
          setEventsError(res.truncated ? 'Historical event detail reached its 20,000-event safety limit. Refine the date range; the displayed breakdowns are partial.' : null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setEventsError(err instanceof Error ? err.message : 'Failed to load events');
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo]);

  useEffect(() => {
    let cancelled = false;
    fetchAnalyticsInsights({
      startDate: sastStartIso(dateFrom),
      endDate: sastEndIso(dateTo),
    })
      .then((result) => { if (!cancelled) { setAnalytics(result); setAnalyticsError(null); } })
      .catch((error: unknown) => { if (!cancelled) setAnalyticsError(error instanceof Error ? error.message : 'New analytics data is unavailable'); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo]);

  const liveOrders = useMemo(
    () => orders.filter((o) => showTestOrders || !isTestOrder(o)),
    [orders, showTestOrders],
  );
  const paidOrders = useMemo(
    () => liveOrders.filter((o) => PAID_STATUSES.has(o.status)),
    [liveOrders],
  );

  const totalRev = useMemo(() => paidOrders.reduce((s, o) => s + (o.totalAmount || 0), 0), [paidOrders]);
  const totalUnits = useMemo(
    () => paidOrders.reduce((s, o) => s + o.items.reduce((ss, i) => ss + i.quantity, 0), 0),
    [paidOrders],
  );
  const aov = paidOrders.length ? totalRev / paidOrders.length : 0;

  const items = useMemo(() => {
    const map = new Map<string, { itemId: string; name: string; qty: number; revenue: number; orders: number }>();
    for (const o of paidOrders) {
      for (const it of o.items) {
        const e = map.get(it.itemId) || { itemId: it.itemId, name: it.name, qty: 0, revenue: 0, orders: 0 };
        e.qty += it.quantity;
        e.revenue += it.lineTotal || it.unitPrice * it.quantity;
        e.orders += 1;
        map.set(it.itemId, e);
      }
    }
    return [...map.values()].sort((a, b) => b.revenue - a.revenue);
  }, [paidOrders]);

  // Reconstruct unique sessions from events: dedupe by sessionId and keep the
  // first-seen client info (utmSource / referrer / userAgent), filling in any
  // gaps from later events in the same session.
  const sessionAttributions = useMemo(() => {
    const map = new Map<
      string,
      {
        sessionId: string;
        utmSource: string | null;
        referrer: string | null;
        userAgent: string | null;
        timezone: string | null;
        language: string | null;
        geoCountry: string | null;
        geoCity: string | null;
        connectionEffectiveType: string | null;
      }
    >();
    const sorted = [...events].sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
    for (const e of sorted) {
      if (!e.sessionId) continue;
      const existing = map.get(e.sessionId);
      if (!existing) {
        map.set(e.sessionId, {
          sessionId: e.sessionId,
          utmSource: e.utmSource ?? null,
          referrer: e.referrer ?? null,
          userAgent: e.userAgent ?? null,
          timezone: e.timezone ?? null,
          language: e.language ?? null,
          geoCountry: e.geoCountry ?? null,
          geoCity: e.geoCity ?? null,
          connectionEffectiveType: e.connectionEffectiveType ?? null,
        });
      } else {
        if (!existing.utmSource && e.utmSource) existing.utmSource = e.utmSource;
        if (!existing.referrer && e.referrer) existing.referrer = e.referrer;
        if (!existing.userAgent && e.userAgent) existing.userAgent = e.userAgent;
        if (!existing.timezone && e.timezone) existing.timezone = e.timezone;
        if (!existing.language && e.language) existing.language = e.language;
        if (!existing.geoCountry && e.geoCountry) existing.geoCountry = e.geoCountry;
        if (!existing.geoCity && e.geoCity) existing.geoCity = e.geoCity;
        if (!existing.connectionEffectiveType && e.connectionEffectiveType) {
          existing.connectionEffectiveType = e.connectionEffectiveType;
        }
      }
    }
    return [...map.values()];
  }, [events]);

  // Channels + devices respect the populationMode segmented control:
  //   "paid"     → paid orders, revenue-weighted (tells you what generates revenue)
  //   "orders"   → all orders (paid + unpaid), counted (intent to buy, even if abandoned)
  //   "sessions" → all unique sessions, counted (every visitor, even bounces)
  const channels = useMemo(() => {
    const map = new Map<string, { name: string; orders: number; revenue: number }>();
    if (populationMode === 'sessions') {
      for (const s of sessionAttributions) {
        const k = channelOfRaw(s);
        const e = map.get(k) || { name: k, orders: 0, revenue: 0 };
        e.orders += 1;
        map.set(k, e);
      }
    } else {
      const set = populationMode === 'paid' ? paidOrders : liveOrders;
      for (const o of set) {
        const k = channelOf(o);
        const e = map.get(k) || { name: k, orders: 0, revenue: 0 };
        e.orders += 1;
        if (populationMode === 'paid') e.revenue += o.totalAmount || 0;
        map.set(k, e);
      }
    }
    return [...map.values()].sort((a, b) =>
      populationMode === 'paid' ? b.revenue - a.revenue : b.orders - a.orders,
    );
  }, [populationMode, paidOrders, liveOrders, sessionAttributions]);

  const devices = useMemo(() => {
    const map = new Map<string, { name: string; orders: number }>();
    if (populationMode === 'sessions') {
      for (const s of sessionAttributions) {
        const k = deviceOfRaw(s);
        const e = map.get(k) || { name: k, orders: 0 };
        e.orders += 1;
        map.set(k, e);
      }
    } else {
      const set = populationMode === 'paid' ? paidOrders : liveOrders;
      for (const o of set) {
        const k = deviceOf(o);
        const e = map.get(k) || { name: k, orders: 0 };
        e.orders += 1;
        map.set(k, e);
      }
    }
    return [...map.values()].sort((a, b) => b.orders - a.orders);
  }, [populationMode, paidOrders, liveOrders, sessionAttributions]);

  const locations = useMemo(() => {
    const map = new Map<string, { name: string; orders: number }>();
    if (populationMode === 'sessions') {
      for (const s of sessionAttributions) {
        const k = locationOfRaw(s);
        const e = map.get(k) || { name: k, orders: 0 };
        e.orders += 1;
        map.set(k, e);
      }
    } else {
      const set = populationMode === 'paid' ? paidOrders : liveOrders;
      for (const o of set) {
        const k = locationOf(o);
        const e = map.get(k) || { name: k, orders: 0 };
        e.orders += 1;
        map.set(k, e);
      }
    }
    return [...map.values()].sort((a, b) => b.orders - a.orders);
  }, [populationMode, paidOrders, liveOrders, sessionAttributions]);

  // Cities — only meaningful where geo.city was captured. Top 10 by count.
  const cities = useMemo(() => {
    const map = new Map<string, { name: string; orders: number }>();
    if (populationMode === 'sessions') {
      for (const s of sessionAttributions) {
        const k = cityOfRaw(s);
        if (!k) continue;
        const e = map.get(k) || { name: k, orders: 0 };
        e.orders += 1;
        map.set(k, e);
      }
    } else {
      const set = populationMode === 'paid' ? paidOrders : liveOrders;
      for (const o of set) {
        const k = cityOf(o);
        if (!k) continue;
        const e = map.get(k) || { name: k, orders: 0 };
        e.orders += 1;
        map.set(k, e);
      }
    }
    return [...map.values()].sort((a, b) => b.orders - a.orders).slice(0, 10);
  }, [populationMode, paidOrders, liveOrders, sessionAttributions]);

  // Network connection bucket (4G / 3G / 2G / Unknown).
  const connections = useMemo(() => {
    const map = new Map<string, { name: string; orders: number }>();
    if (populationMode === 'sessions') {
      for (const s of sessionAttributions) {
        const k = connectionOfRaw(s);
        const e = map.get(k) || { name: k, orders: 0 };
        e.orders += 1;
        map.set(k, e);
      }
    } else {
      const set = populationMode === 'paid' ? paidOrders : liveOrders;
      for (const o of set) {
        const k = connectionOf(o);
        const e = map.get(k) || { name: k, orders: 0 };
        e.orders += 1;
        map.set(k, e);
      }
    }
    return [...map.values()].sort((a, b) => b.orders - a.orders);
  }, [populationMode, paidOrders, liveOrders, sessionAttributions]);

  // Page paths — count of events at each pathname. Top 10. Independent of
  // population mode (this is event-count, not order- or session-count).
  const paths = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of events) {
      const p = (e.pathname || '').trim() || '(unknown)';
      map.set(p, (map.get(p) || 0) + 1);
    }
    return [...map.entries()]
      .map(([path, count]) => ({ path, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [events]);

  const statusBreakdown = useMemo(() => {
    const order: Array<{ key: string; color: string }> = [
      { key: 'Pending', color: '#ca8a04' },
      { key: 'Verifying', color: '#0891b2' },
      { key: 'Paid', color: '#ea580c' },
      { key: 'Ready', color: '#7c3aed' },
      { key: 'Completed', color: '#16a34a' },
    ];
    const counts = new Map<string, number>();
    for (const o of liveOrders) counts.set(o.status, (counts.get(o.status) || 0) + 1);
    return order.map(({ key, color }) => ({ status: key, orders: counts.get(key) || 0, color }));
  }, [liveOrders]);

  const leadTime = useMemo(() => {
    const map = new Map<number, number>();
    for (const o of paidOrders) {
      if (!o.eventDate) continue;
      const created = sastDay(o.createdAt);
      if (!created) continue;
      const days = Math.round(
        (new Date(`${o.eventDate}T00:00:00Z`).getTime() - new Date(`${created}T00:00:00Z`).getTime()) / 86400000,
      );
      if (days < 0) continue;
      map.set(days, (map.get(days) || 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[0] - a[0]).map(([days, orders]) => ({ label: `T-${days}`, days, orders }));
  }, [paidOrders]);

  const cumRev = useMemo(() => {
    const map = new Map<string, number>();
    for (const o of paidOrders) {
      const d = sastDay(o.createdAt);
      if (!d) continue;
      map.set(d, (map.get(d) || 0) + (o.totalAmount || 0));
    }
    const days = [...map.keys()].sort();
    let running = 0;
    return days.map((d) => {
      running += map.get(d) || 0;
      return { day: d.slice(5), dayFull: d, daily: map.get(d) || 0, cumulative: running };
    });
  }, [paidOrders]);

  const funnel = useMemo(() => {
    const sess = new Map<string, { open: boolean; checkout: boolean; orderCreated: boolean; payment: boolean; paid: boolean }>();
    for (const e of events) {
      if (!e.sessionId) continue;
      const s = sess.get(e.sessionId) || { open: false, checkout: false, orderCreated: false, payment: false, paid: false };
      if (e.eventType === 'app_open') s.open = true;
      if (e.eventType === 'checkout_started') s.checkout = true;
      if (e.eventType === 'order_created') s.orderCreated = true;
      if (e.eventType === 'payment_started') s.payment = true;
      sess.set(e.sessionId, s);
    }
    const paidSids = new Set(paidOrders.map((o) => o.metadata?.sessionId).filter((x): x is string => Boolean(x)));
    for (const sid of paidSids) {
      const s = sess.get(sid);
      if (s) s.paid = true;
    }
    const stages: Array<['open' | 'checkout' | 'orderCreated' | 'payment' | 'paid', string]> = [
      ['open', 'App open'],
      ['checkout', 'Checkout'],
      ['orderCreated', 'Order created'],
      ['payment', 'Payment'],
      ['paid', 'Paid'],
    ];
    return stages.map(([k, label]) => {
      let n = 0;
      for (const s of sess.values()) if (s[k]) n += 1;
      return { stage: label, sessions: n };
    });
  }, [events, paidOrders]);

  const drillOrders = useMemo(() => {
    if (!drill) return [];
    return liveOrders.filter((o) => {
      switch (drill.field) {
        case 'item':
          return o.items.some((i) => i.itemId === drill.value);
        case 'channel':
          return channelOf(o) === drill.value;
        case 'device':
          return deviceOf(o) === drill.value;
        case 'location':
          return locationOf(o) === drill.value;
        case 'city':
          return cityOf(o) === drill.value;
        case 'connection':
          return connectionOf(o) === drill.value;
        case 'status':
          return o.status === drill.value;
        case 'leadDays': {
          if (!o.eventDate) return false;
          const created = sastDay(o.createdAt);
          if (!created) return false;
          const days = Math.round(
            (new Date(`${o.eventDate}T00:00:00Z`).getTime() - new Date(`${created}T00:00:00Z`).getTime()) / 86400000,
          );
          return days === drill.value;
        }
        case 'day':
          return sastDay(o.createdAt) === drill.value;
        default:
          return false;
      }
    });
  }, [drill, liveOrders]);

  const topItem = items[0];
  const topChannel = channels[0];
  const opens = funnel[0]?.sessions || 0;
  const paid = funnel[funnel.length - 1]?.sessions || 0;
  const checkouts = funnel[1]?.sessions || 0;
  const convTop = opens ? (paid * 100) / opens : 0;
  const convCheckout = checkouts ? (paid * 100) / checkouts : 0;

  if (liveOrders.length === 0) {
    return (
      <div className="space-y-4">
        <Card>
          <h2 className="text-lg font-semibold text-admin-text">Insights</h2>
          <p className="text-sm text-admin-text/70 mt-1">
            No orders in the selected date range. Adjust the filter and try again.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {analytics && analytics.metrics.events > 0 && (
        <Card>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-admin-text">Campaign intelligence</h2>
              <p className="text-sm text-admin-text/70 mt-1">First-party analytics collected with the current tracking schema.</p>
            </div>
            <div className="text-xs text-admin-text/60">Updated {new Date(analytics.generatedAt).toLocaleString('en-ZA')}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mt-4">
            <Kpi label="Tracked browsers" value={analytics.metrics.trackedBrowsers.toLocaleString()} hint="Pseudonymous, not people" />
            <Kpi label="Sessions" value={analytics.metrics.sessions.toLocaleString()} hint="30-minute inactivity boundary" />
            <Kpi label="Engaged sessions" value={analytics.metrics.engagedSessions.toLocaleString()} hint={pct(analytics.metrics.engagedSessions, analytics.metrics.sessions)} />
            <Kpi label="Checkout sessions" value={analytics.metrics.checkoutSessions.toLocaleString()} hint={pct(analytics.metrics.checkoutSessions, analytics.metrics.sessions)} />
            <Kpi label="Purchasing sessions" value={analytics.metrics.purchasingSessions.toLocaleString()} hint={pct(analytics.metrics.purchasingSessions, analytics.metrics.sessions)} />
            <Kpi label="Matched revenue" value={fmtR(analytics.metrics.revenue)} hint={`${analytics.metrics.confirmedOrders} confirmed orders`} />
          </div>
          <div className="grid md:grid-cols-3 gap-4 mt-5 text-xs">
            {([
              ['Acquisition', analytics.breakdowns.acquisition],
              ['Device', analytics.breakdowns.devices],
              ['Operating system', analytics.breakdowns.operatingSystems],
            ] as const).map(([title, rows]) => (
              <div key={title}>
                <div className="font-semibold text-admin-text mb-1.5">{title}</div>
                {rows.slice(0, 4).map((row) => <div key={row.name} className="flex justify-between gap-3 py-0.5 text-admin-text/70"><span className="truncate">{row.name}</span><span className="tabular-nums">{row.value}</span></div>)}
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-admin-text/60">Coverage: campaign {analytics.coverage.campaignContext == null ? 'not measured' : pct(analytics.coverage.campaignContext, 1)}, attribution {analytics.coverage.attribution == null ? 'not measured' : pct(analytics.coverage.attribution, 1)}, location {analytics.coverage.geo == null ? 'not measured' : pct(analytics.coverage.geo, 1)}. Age and gender are not collected.</p>
        </Card>
      )}
      {analyticsError && <Card><p className="text-sm text-admin-text/70">New campaign intelligence is unavailable: {analyticsError}</p></Card>}
      <Card>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-semibold text-admin-text">Insights</h2>
            <p className="text-sm text-admin-text/70">
              {dateFrom || '…'} → {dateTo || '…'} · {paidOrders.length} paid order{paidOrders.length === 1 ? '' : 's'}
              {showTestOrders ? ' · including test mode' : ''}
            </p>
          </div>
          {eventsLoading && <span className="text-xs text-admin-text/60">Loading events…</span>}
          {eventsError && <span className="text-xs text-red-600">Events: {eventsError}</span>}
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Revenue" value={fmtR(totalRev)} hint={`${paidOrders.length} orders`} />
        <Kpi label="Average order" value={fmtR(aov)} hint={`${totalUnits} units`} />
        <Kpi
          label="Top item share"
          value={topItem ? pct(topItem.revenue, totalRev) : '—'}
          hint={topItem?.name}
        />
        <Kpi
          label="Top channel"
          value={topChannel ? pct(topChannel.revenue, totalRev) : '—'}
          hint={topChannel?.name}
        />
        <Kpi
          label="Open → Paid"
          value={opens ? convTop.toFixed(1) + '%' : '—'}
          hint={checkouts ? `${convCheckout.toFixed(0)}% from checkout` : 'awaiting events'}
        />
      </div>

      {drill && (
        <Card>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-admin-accent">Drill down</div>
              <div className="text-base font-semibold text-admin-text mt-0.5">
                {drill.label} · {drillOrders.length} order{drillOrders.length === 1 ? '' : 's'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setDrill(null)}
              className="text-sm font-medium text-admin-muted hover:text-admin-text transition-colors"
            >
              Clear ✕
            </button>
          </div>
          {drillOrders.length === 0 ? (
            <div className="text-sm text-admin-text/60 py-4 text-center">No orders match.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-admin-text/60">
                  <tr className="border-b border-admin-text/10">
                    <th className="text-left py-2 pr-3 font-semibold">Order</th>
                    <th className="text-left py-2 px-3 font-semibold">Status</th>
                    <th className="text-left py-2 px-3 font-semibold">Type</th>
                    <th className="text-left py-2 px-3 font-semibold">Created</th>
                    <th className="text-right py-2 px-3 font-semibold">Items</th>
                    <th className="text-right py-2 pl-3 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {drillOrders.slice(0, 100).map((o) => (
                    <tr key={o.orderId} className="border-b border-admin-text/5">
                      <td className="py-2 pr-3 font-mono text-xs text-admin-text">{o.orderId}</td>
                      <td className="py-2 px-3 text-admin-text">{o.status}</td>
                      <td className="py-2 px-3 text-admin-text">{o.orderType}</td>
                      <td className="py-2 px-3 text-admin-text/70">
                        {new Date(o.createdAt).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {o.items.reduce((s, i) => s + i.quantity, 0)}
                      </td>
                      <td className="py-2 pl-3 text-right tabular-nums">{fmtR(o.totalAmount || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {drillOrders.length > 100 && (
                <div className="text-xs text-admin-text/60 mt-2">Showing first 100 of {drillOrders.length}.</div>
              )}
            </div>
          )}
        </Card>
      )}

      <ChartCard title="Revenue by item" subtitle="Paid orders only" height={Math.max(220, items.length * 36)}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={items}
            layout="vertical"
            margin={{ left: 16, right: 80, top: 8, bottom: 8 }}
            onClick={(state: { activePayload?: { payload?: { itemId: string; name: string } }[] }) => {
              const row = state?.activePayload?.[0]?.payload;
              if (row) setDrill({ field: 'item', value: row.itemId, label: `Item: ${row.name}` });
            }}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid horizontal={false} stroke="#f5f5f4" />
            <XAxis type="number" tickFormatter={(v) => fmtR(v as number)} stroke="#78716c" fontSize={11} />
            <YAxis type="category" dataKey="name" stroke="#57534e" fontSize={12} width={180} />
            <Tooltip
              formatter={(v: number, _n, p) => [`${fmtR(v)} · ${p.payload.qty} units`, 'Revenue']}
              labelStyle={{ color: '#1c1917', fontWeight: 600 }}
            />
            <Bar dataKey="revenue" radius={[0, 6, 6, 0]}>
              {items.map((_, i) => (
                <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
              ))}
              <LabelList
                dataKey="revenue"
                position="right"
                formatter={(v: number) => fmtR(v)}
                style={{ fontSize: 11, fill: '#1c1917', fontWeight: 600 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-admin-text">Channels &amp; devices</h3>
        <div
          className="inline-flex items-stretch rounded-md border border-admin-border bg-admin-surface text-xs overflow-hidden"
          role="tablist"
          aria-label="Population mode"
        >
          {(['paid', 'orders', 'sessions'] as const).map((mode, i) => {
            const active = populationMode === mode;
            const label = mode === 'paid' ? 'Paid orders' : mode === 'orders' ? 'All orders' : 'All sessions';
            return (
              <button
                key={mode}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setPopulationMode(mode)}
                className={`px-3 py-1.5 transition-colors ${i > 0 ? 'border-l border-admin-border' : ''} ${
                  active ? 'bg-admin-accent-soft text-admin-accent font-medium' : 'text-admin-muted hover:text-admin-text'
                }`}
                title={
                  mode === 'paid'
                    ? 'Revenue-weighted, paid orders only'
                    : mode === 'orders'
                      ? 'Order count, including unpaid (Pending/Verifying)'
                      : 'Unique sessions from events — every visitor, even those who never ordered'
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <ChartCard
          title="Channel mix"
          subtitle={
            populationMode === 'paid'
              ? `Revenue across ${paidOrders.length} paid order${paidOrders.length === 1 ? '' : 's'}`
              : populationMode === 'orders'
                ? `Order count across ${liveOrders.length} order${liveOrders.length === 1 ? '' : 's'} (paid + unpaid)`
                : `Across ${sessionAttributions.length} unique session${sessionAttributions.length === 1 ? '' : 's'}`
          }
          height={220}
          footer={
            <Breakdown
              rows={channels.map((c, i) => ({
                name: c.name,
                color: PALETTE[i % PALETTE.length],
                value: populationMode === 'paid' ? c.revenue : c.orders,
                count: populationMode === 'paid' ? c.orders : undefined,
                total:
                  populationMode === 'paid'
                    ? totalRev
                    : channels.reduce((s, x) => s + x.orders, 0),
              }))}
              valueFmt={
                populationMode === 'paid'
                  ? fmtR
                  : populationMode === 'sessions'
                    ? (v) => `${v} session${v === 1 ? '' : 's'}`
                    : (v) => `${v} order${v === 1 ? '' : 's'}`
              }
            />
          }
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={channels}
                dataKey={populationMode === 'paid' ? 'revenue' : 'orders'}
                nameKey="name"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                label={renderPieLabel('percent')}
                labelLine={false}
                cursor="pointer"
                onClick={(d: { name: string }) =>
                  setDrill({ field: 'channel', value: d.name, label: `Channel: ${d.name}` })
                }
              >
                {channels.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v: number) => (populationMode === 'paid' ? fmtR(v) : `${v}`)}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Device"
          subtitle={
            populationMode === 'paid'
              ? `Across ${paidOrders.length} paid order${paidOrders.length === 1 ? '' : 's'}`
              : populationMode === 'orders'
                ? `Across ${liveOrders.length} order${liveOrders.length === 1 ? '' : 's'} (paid + unpaid)`
                : `Across ${sessionAttributions.length} unique session${sessionAttributions.length === 1 ? '' : 's'}`
          }
          height={220}
          footer={
            <Breakdown
              rows={devices.map((d, i) => ({
                name: d.name,
                color: PALETTE[i % PALETTE.length],
                value: d.orders,
                total:
                  populationMode === 'paid'
                    ? paidOrders.length
                    : populationMode === 'orders'
                      ? liveOrders.length
                      : sessionAttributions.length,
              }))}
              valueFmt={
                populationMode === 'sessions'
                  ? (v) => `${v} session${v === 1 ? '' : 's'}`
                  : (v) => `${v} order${v === 1 ? '' : 's'}`
              }
            />
          }
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={devices}
                dataKey="orders"
                nameKey="name"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                label={renderPieLabel('value')}
                labelLine={false}
                cursor="pointer"
                onClick={(d: { name: string }) =>
                  setDrill({ field: 'device', value: d.name, label: `Device: ${d.name}` })
                }
              >
                {devices.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Location"
          subtitle={
            populationMode === 'paid'
              ? `Region across ${paidOrders.length} paid order${paidOrders.length === 1 ? '' : 's'}`
              : populationMode === 'orders'
                ? `Region across ${liveOrders.length} order${liveOrders.length === 1 ? '' : 's'} (paid + unpaid)`
                : `Region across ${sessionAttributions.length} unique session${sessionAttributions.length === 1 ? '' : 's'}`
          }
          height={220}
          footer={
            <Breakdown
              rows={locations.map((l, i) => ({
                name: l.name,
                color: PALETTE[i % PALETTE.length],
                value: l.orders,
                total:
                  populationMode === 'paid'
                    ? paidOrders.length
                    : populationMode === 'orders'
                      ? liveOrders.length
                      : sessionAttributions.length,
              }))}
              valueFmt={
                populationMode === 'sessions'
                  ? (v) => `${v} session${v === 1 ? '' : 's'}`
                  : (v) => `${v} order${v === 1 ? '' : 's'}`
              }
            />
          }
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={locations}
                dataKey="orders"
                nameKey="name"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                label={renderPieLabel('percent')}
                labelLine={false}
                cursor="pointer"
                onClick={(d: { name: string }) =>
                  setDrill({ field: 'location', value: d.name, label: `Location: ${d.name}` })
                }
              >
                {locations.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Connection"
          subtitle={
            populationMode === 'paid'
              ? `Across ${paidOrders.length} paid order${paidOrders.length === 1 ? '' : 's'}`
              : populationMode === 'orders'
                ? `Across ${liveOrders.length} order${liveOrders.length === 1 ? '' : 's'} (paid + unpaid)`
                : `Across ${sessionAttributions.length} unique session${sessionAttributions.length === 1 ? '' : 's'}`
          }
          height={220}
          footer={
            <Breakdown
              rows={connections.map((c, i) => ({
                name: c.name,
                color: PALETTE[i % PALETTE.length],
                value: c.orders,
                total:
                  populationMode === 'paid'
                    ? paidOrders.length
                    : populationMode === 'orders'
                      ? liveOrders.length
                      : sessionAttributions.length,
              }))}
              valueFmt={
                populationMode === 'sessions'
                  ? (v) => `${v} session${v === 1 ? '' : 's'}`
                  : (v) => `${v} order${v === 1 ? '' : 's'}`
              }
            />
          }
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={connections}
                dataKey="orders"
                nameKey="name"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                label={renderPieLabel('value')}
                labelLine={false}
                cursor="pointer"
                onClick={(d: { name: string }) =>
                  setDrill({ field: 'connection', value: d.name, label: `Connection: ${d.name}` })
                }
              >
                {connections.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {cities.length > 0 && (
        <ChartCard
          title="Cities"
          subtitle={
            populationMode === 'paid'
              ? `Top ${cities.length} cities · paid orders`
              : populationMode === 'orders'
                ? `Top ${cities.length} cities · all orders`
                : `Top ${cities.length} cities · sessions`
          }
          height={Math.max(180, cities.length * 32)}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={cities}
              layout="vertical"
              margin={{ left: 16, right: 60, top: 8, bottom: 8 }}
              onClick={(state: { activePayload?: { payload?: { name: string } }[] }) => {
                const row = state?.activePayload?.[0]?.payload;
                if (row) setDrill({ field: 'city', value: row.name, label: `City: ${row.name}` });
              }}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid horizontal={false} stroke="#f5f5f4" />
              <XAxis type="number" stroke="#78716c" fontSize={11} allowDecimals={false} />
              <YAxis type="category" dataKey="name" stroke="#57534e" fontSize={12} width={200} />
              <Tooltip
                formatter={(v: number) => [
                  populationMode === 'sessions'
                    ? `${v} session${v === 1 ? '' : 's'}`
                    : `${v} order${v === 1 ? '' : 's'}`,
                  'Count',
                ]}
              />
              <Bar dataKey="orders" radius={[0, 6, 6, 0]}>
                {cities.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
                <LabelList
                  dataKey="orders"
                  position="right"
                  style={{ fontSize: 11, fill: '#1c1917', fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <ChartCard
        title="Conversion funnel"
        subtitle={
          eventsLoading
            ? 'Loading events…'
            : eventsError
              ? `Error loading events: ${eventsError}`
              : `${events.length.toLocaleString()} event${events.length === 1 ? '' : 's'} captured · ${opens.toLocaleString()} unique session${opens === 1 ? '' : 's'}`
        }
        height={260}
      >
        {opens === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-sm text-admin-text/60 gap-1">
            {events.length === 0 ? (
              <>
                <div>No events captured in this date range.</div>
                <div className="text-xs">Widen the date filter or pick a window with traffic.</div>
              </>
            ) : (
              <>
                <div>Events received but no app_open sessions.</div>
                <div className="text-xs">Funnel needs `app_open` events to anchor the top of funnel.</div>
              </>
            )}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={funnel} layout="vertical" margin={{ left: 16, right: 110, top: 8, bottom: 8 }}>
              <CartesianGrid horizontal={false} stroke="#f5f5f4" />
              <XAxis type="number" stroke="#78716c" fontSize={11} />
              <YAxis type="category" dataKey="stage" stroke="#57534e" fontSize={12} width={120} />
              <Tooltip
                formatter={(v: number) => [
                  `${v.toLocaleString()} sessions${opens ? ` · ${((v * 100) / opens).toFixed(1)}% of opens` : ''}`,
                  'Count',
                ]}
              />
              <Bar dataKey="sessions" radius={[0, 6, 6, 0]}>
                {funnel.map((_, i) => (
                  <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />
                ))}
                <LabelList
                  dataKey="sessions"
                  position="right"
                  formatter={(v: number) =>
                    opens ? `${v.toLocaleString()} (${((v * 100) / opens).toFixed(1)}%)` : v.toLocaleString()
                  }
                  style={{ fontSize: 11, fill: '#1c1917', fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {paths.length > 0 && (
        <ChartCard
          title="Page activity"
          subtitle={`Top ${paths.length} pathname${paths.length === 1 ? '' : 's'} by event count · ${events.length.toLocaleString()} total events`}
          height={Math.max(180, paths.length * 32)}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={paths} layout="vertical" margin={{ left: 16, right: 60, top: 8, bottom: 8 }}>
              <CartesianGrid horizontal={false} stroke="#f5f5f4" />
              <XAxis type="number" stroke="#78716c" fontSize={11} allowDecimals={false} />
              <YAxis type="category" dataKey="path" stroke="#57534e" fontSize={11} width={220} />
              <Tooltip formatter={(v: number) => [`${v} event${v === 1 ? '' : 's'}`, 'Count']} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]} fill={PALETTE[3]}>
                <LabelList
                  dataKey="count"
                  position="right"
                  style={{ fontSize: 11, fill: '#1c1917', fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ChartCard title="Order status" subtitle="All orders in range, by status">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={statusBreakdown}
              margin={{ left: 4, right: 16, top: 24, bottom: 8 }}
              onClick={(state: { activePayload?: { payload?: { status: string } }[] }) => {
                const row = state?.activePayload?.[0]?.payload;
                if (row) setDrill({ field: 'status', value: row.status, label: `Status: ${row.status}` });
              }}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid vertical={false} stroke="#f5f5f4" />
              <XAxis dataKey="status" stroke="#78716c" fontSize={11} />
              <YAxis stroke="#78716c" fontSize={11} allowDecimals={false} />
              <Tooltip formatter={(v: number) => [v, 'Orders']} />
              <Bar dataKey="orders" radius={[6, 6, 0, 0]}>
                {statusBreakdown.map((s, i) => (
                  <Cell key={i} fill={s.color} />
                ))}
                <LabelList
                  dataKey="orders"
                  position="top"
                  formatter={(v: number) =>
                    v === 0 ? '' : `${v} (${Math.round((v / liveOrders.length) * 100)}%)`
                  }
                  style={{ fontSize: 11, fill: '#1c1917', fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Lead time"
          subtitle={leadTime.length ? 'Days between order and event' : 'No eventDate on orders in range'}
        >
          {leadTime.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={leadTime}
                margin={{ left: 4, right: 16, top: 24, bottom: 8 }}
                onClick={(state: { activePayload?: { payload?: { days: number; label: string } }[] }) => {
                  const row = state?.activePayload?.[0]?.payload;
                  if (row) setDrill({ field: 'leadDays', value: row.days, label: `Lead time: ${row.label}` });
                }}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid vertical={false} stroke="#f5f5f4" />
                <XAxis dataKey="label" stroke="#78716c" fontSize={11} />
                <YAxis stroke="#78716c" fontSize={11} allowDecimals={false} />
                <Tooltip formatter={(v: number) => [v, 'Orders']} />
                <Bar dataKey="orders" fill={PALETTE[3]} radius={[6, 6, 0, 0]}>
                  <LabelList
                    dataKey="orders"
                    position="top"
                    formatter={(v: number) => {
                      const total = leadTime.reduce((s, l) => s + l.orders, 0);
                      return total ? `${v} (${Math.round((v / total) * 100)}%)` : String(v);
                    }}
                    style={{ fontSize: 11, fill: '#1c1917', fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-admin-text/50">
              No event-date data
            </div>
          )}
        </ChartCard>
      </div>

      <ChartCard title="Cumulative revenue" subtitle="Daily bars + running total">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={cumRev}
              margin={{ left: 4, right: 32, top: 28, bottom: 8 }}
              onClick={(state: { activePayload?: { payload?: { dayFull: string; day: string } }[] }) => {
                const row = state?.activePayload?.[0]?.payload;
                if (row) setDrill({ field: 'day', value: row.dayFull, label: `Day: ${row.dayFull}` });
              }}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid vertical={false} stroke="#f5f5f4" />
              <XAxis dataKey="day" stroke="#78716c" fontSize={11} />
              <YAxis stroke="#78716c" fontSize={11} tickFormatter={(v) => fmtR(v as number)} />
              <Tooltip formatter={(v: number) => fmtR(v)} />
              <Legend verticalAlign="bottom" height={28} iconType="circle" />
              <Bar dataKey="daily" name="Daily" fill="#fed7aa" radius={[4, 4, 0, 0]} />
              <Line
                type="monotone"
                dataKey="cumulative"
                name="Cumulative"
                stroke={PALETTE[0]}
                strokeWidth={2}
                dot={{ r: 3 }}
              >
                <LabelList
                  dataKey="cumulative"
                  position="top"
                  formatter={(v: number) => fmtR(v)}
                  style={{ fontSize: 10, fill: '#1c1917', fontWeight: 600 }}
                />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

      <Card>
        <div className="text-sm font-semibold text-admin-text mb-2">Items</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-admin-text/60">
              <tr className="border-b border-admin-text/10">
                <th className="text-left py-2 pr-3 font-semibold">Item</th>
                <th className="text-right py-2 px-3 font-semibold">Units</th>
                <th className="text-right py-2 px-3 font-semibold">Orders</th>
                <th className="text-right py-2 px-3 font-semibold">Revenue</th>
                <th className="text-right py-2 pl-3 font-semibold">% of rev</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.name} className="border-b border-admin-text/5">
                  <td className="py-2 pr-3 text-admin-text">{it.name}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{it.qty}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{it.orders}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmtR(it.revenue)}</td>
                  <td className="py-2 pl-3 text-right tabular-nums">{pct(it.revenue, totalRev)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
